import { FormEvent, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { useRouter } from "next/router";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/loading-button";
import { getApiErrorMessage } from "@/store/api";
import { useSubmitFeedbackMutation } from "@/store/utility-api";

export function FeedbackButton() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [submitFeedback, { error, isLoading, isSuccess, reset }] =
    useSubmitFeedbackMutation();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      await submitFeedback({
        sessionId: getSessionId(router.query.sessionId),
        text,
      }).unwrap();
      setText("");
    } catch {
      return;
    }
  };

  if (!open) {
    return (
      <Button
        className="feedback-trigger"
        size="icon"
        aria-label="Send feedback"
        onClick={() => {
          setOpen(true);
          reset();
        }}
      >
        <MessageCircle />
      </Button>
    );
  }

  return (
    <aside className="feedback-popover" aria-label="Send feedback">
      <header>
        <strong>Feedback</strong>
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label="Close feedback"
          onClick={() => setOpen(false)}
        >
          <X />
        </Button>
      </header>
      {isSuccess ? (
        <p role="status">Thanks — feedback sent.</p>
      ) : (
        <form onSubmit={submit}>
          <label htmlFor="feedback-text">What could be better?</label>
          <textarea
            id="feedback-text"
            value={text}
            onChange={(event) => setText(event.target.value)}
            maxLength={2_000}
            required
          />
          {error && (
            <p className="form-error" role="alert">
              {getApiErrorMessage(error, "Could not send feedback")}
            </p>
          )}
          <LoadingButton type="submit" size="sm" loading={isLoading}>
            Send
          </LoadingButton>
        </form>
      )}
    </aside>
  );
}

function getSessionId(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}
